import { all, call, put, takeLatest, delay } from 'redux-saga/effects'
import VideoActions, { VideoTypes } from 'Redux/VideoRedux'

// Create Video
export function* createVideo(api, action) {
  try {
    const { scriptId, audioUrl, segments, options } = action
    const { subtitlesEnabled = false, subtitleStyle = 'default', subtitleSettings = null, soundEffectClips = [] } = options || {}
    
    const params = {
      audio_url: audioUrl,
      subtitles_enabled: subtitlesEnabled,
      subtitle_style: subtitleStyle,
      subtitle_settings: subtitleSettings,
      segments: segments.map(seg => ({
        image_url: seg.image_url,
        animated_video_url: seg.animated_video_url || null,
        start_time: seg.startTime,
        end_time: seg.endTime,
      })),
      sound_effect_clips: soundEffectClips.map(clip => ({
        provider_sound_id: clip.asset?.provider_sound_id || clip.provider_sound_id,
        name: clip.asset?.name || clip.name,
        preview_url: clip.asset?.preview_url || clip.preview_url,
        audio_url: clip.asset?.audio_url || clip.audio_url,
        duration: clip.asset?.duration || clip.duration,
        category: clip.asset?.category || clip.category,
        start_time: clip.start_time,
        end_time: clip.end_time,
        loop_enabled: clip.loop_enabled,
        volume: clip.volume,
        layer_index: clip.layer_index || 0,
      })),
    }

    const resp = yield call(api.createVideo, scriptId, params)

    if (resp.ok) {
      yield put(VideoActions.videoCreateSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to create video'
      yield put(VideoActions.videoCreateFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to create video'
    yield put(VideoActions.videoCreateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Video
export function* getVideo(api, action) {
  try {
    const resp = yield call(api.getVideo, action.id)

    if (resp.ok) {
      yield put(VideoActions.videoGetSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to get video'
      yield put(VideoActions.videoGetFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to get video'
    yield put(VideoActions.videoGetFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Video Status
export function* getVideoStatus(api, action) {
  try {
    const resp = yield call(api.getVideoStatus, action.id)

    if (resp.ok) {
      yield put(VideoActions.videoStatusSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to get video status'
      yield put(VideoActions.videoStatusFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to get video status'
    yield put(VideoActions.videoStatusFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// List Videos for Script
export function* listVideos(api, action) {
  try {
    const resp = yield call(api.getVideosForScript, action.scriptId)

    if (resp.ok) {
      yield put(VideoActions.videoListSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to list videos'
      yield put(VideoActions.videoListFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to list videos'
    yield put(VideoActions.videoListFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Poll Video Until Complete
export function* pollVideo(api, action) {
  const { id, interval: pollInterval = 10000, callback } = action
  const maxAttempts = 120 // 20 minutes max at 10s interval
  
  let attempts = 0
  
  try {
    while (attempts < maxAttempts) {
      const resp = yield call(api.getVideoStatus, id)
      
      if (!resp.ok) {
        const error = resp.data?.error || 'Failed to get video status'
        yield put(VideoActions.videoPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      const status = resp.data
      yield put(VideoActions.videoPollProgress(status))
      
      if (status.status === 'completed') {
        // Fetch full video data
        const fullResp = yield call(api.getVideo, id)
        if (fullResp.ok) {
          yield put(VideoActions.videoPollSuccess(fullResp.data))
          if (callback) {
            callback(fullResp.data, null)
          }
        } else {
          if (callback) {
            callback(null, 'Failed to load completed video')
          }
        }
        return
      }
      
      if (status.status === 'failed') {
        const error = status.error_message || 'Video generation failed'
        yield put(VideoActions.videoPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      attempts++
      yield delay(pollInterval)
    }
    
    // Timeout
    const error = 'Video generation timed out'
    yield put(VideoActions.videoPollFailure(error))
    if (callback) {
      callback(null, error)
    }
  } catch (error) {
    const errorMsg = error.message || 'Polling failed'
    yield put(VideoActions.videoPollFailure(errorMsg))
    if (callback) {
      callback(null, errorMsg)
    }
  }
}

// Generate Video (create + poll)
export function* generateVideo(api, action) {
  const { scriptId, audioUrl, segments, options, callback } = action
  const { subtitlesEnabled = false, subtitleStyle = 'default', subtitleSettings = null, soundEffectClips = [] } = options || {}
  
  try {
    // Create video
    const params = {
      audio_url: audioUrl,
      subtitles_enabled: subtitlesEnabled,
      subtitle_style: subtitleStyle,
      subtitle_settings: subtitleSettings,
      segments: segments.map(seg => ({
        image_url: seg.image_url,
        animated_video_url: seg.animated_video_url || null,
        start_time: seg.startTime,
        end_time: seg.endTime,
      })),
      sound_effect_clips: soundEffectClips.map(clip => ({
        provider_sound_id: clip.asset?.provider_sound_id || clip.provider_sound_id,
        name: clip.asset?.name || clip.name,
        preview_url: clip.asset?.preview_url || clip.preview_url,
        audio_url: clip.asset?.audio_url || clip.audio_url,
        duration: clip.asset?.duration || clip.duration,
        category: clip.asset?.category || clip.category,
        start_time: clip.start_time,
        end_time: clip.end_time,
        loop_enabled: clip.loop_enabled,
        volume: clip.volume,
        layer_index: clip.layer_index || 0,
      })),
    }
    
    const createResp = yield call(api.createVideo, scriptId, params)
    
    if (!createResp.ok) {
      const error = createResp.data?.error || 'Failed to create video'
      yield put(VideoActions.videoGenerateFailure(error))
      if (callback) {
        callback(null, error)
      }
      return
    }
    
    const videoId = createResp.data.id
    
    // Poll until complete
    const interval = 10000 // 10 seconds
    const maxAttempts = 120
    let attempts = 0
    
    while (attempts < maxAttempts) {
      const statusResp = yield call(api.getVideoStatus, videoId)
      
      if (!statusResp.ok) {
        const error = statusResp.data?.error || 'Failed to get video status'
        yield put(VideoActions.videoGenerateFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      const status = statusResp.data
      yield put(VideoActions.videoGenerateProgress(status))
      
      if (status.status === 'completed') {
        const fullResp = yield call(api.getVideo, videoId)
        if (fullResp.ok) {
          yield put(VideoActions.videoGenerateSuccess(fullResp.data))
          if (callback) {
            callback(fullResp.data, null)
          }
        } else {
          const error = 'Failed to load completed video'
          yield put(VideoActions.videoGenerateFailure(error))
          if (callback) {
            callback(null, error)
          }
        }
        return
      }
      
      if (status.status === 'failed') {
        const error = status.error_message || 'Video generation failed'
        yield put(VideoActions.videoGenerateFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      attempts++
      yield delay(interval)
    }
    
    // Timeout
    const error = 'Video generation timed out'
    yield put(VideoActions.videoGenerateFailure(error))
    if (callback) {
      callback(null, error)
    }
  } catch (error) {
    const errorMsg = error.message || 'Video generation failed'
    yield put(VideoActions.videoGenerateFailure(errorMsg))
    if (callback) {
      callback(null, errorMsg)
    }
  }
}

// Watcher saga
export default function* videoSaga(api) {
  yield all([
    takeLatest(VideoTypes.VIDEO_CREATE_REQUEST, createVideo, api),
    takeLatest(VideoTypes.VIDEO_GET_REQUEST, getVideo, api),
    takeLatest(VideoTypes.VIDEO_STATUS_REQUEST, getVideoStatus, api),
    takeLatest(VideoTypes.VIDEO_LIST_REQUEST, listVideos, api),
    takeLatest(VideoTypes.VIDEO_POLL_REQUEST, pollVideo, api),
    takeLatest(VideoTypes.VIDEO_GENERATE_REQUEST, generateVideo, api),
  ])
}
